import { useState, useEffect, useCallback, useRef } from 'react'
import Navbar from '../components/Navbar'
import LocationScroller from '../components/LocationScroller'
import Footer from '../components/Footer'
import PropertyCard from '../components/PropertyCard'
import OffplanFilterBar from '../components/OffplanFilterBar'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"
const PRICE_MIN_ABS = 300_000
const PRICE_MAX_ABS = 50_000_000
const API_URL = import.meta.env.VITE_API_URL || ''

/* ─── Default filter state ────────────────────────────────────────────── */
const DEFAULT_FILTERS = {
  searchQuery: '', minPrice: PRICE_MIN_ABS, maxPrice: PRICE_MAX_ABS,
  bedrooms: [], areas: [], emirates: [], lifestyles: [], developer: '',
  currency: 'AED', areaUnit: 'sqft', sortBy: 'default',
}


// ─── Backend handles sorting ──────────────────────────────────────────────

/* ─── Skeleton card ───────────────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div style={{
      borderRadius: '16px', overflow: 'hidden', background: '#fff',
      border: '1px solid rgba(0,46,83,0.08)', boxShadow: '0 4px 16px rgba(0,46,83,0.06)',
    }}>
      <div style={{ height: '220px', background: 'linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ height: '20px', width: '70%', background: '#e2e8f0', borderRadius: '6px' }} />
        <div style={{ height: '14px', width: '50%', background: '#e2e8f0', borderRadius: '6px' }} />
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ height: '24px', width: '80px', background: '#e2e8f0', borderRadius: '6px' }} />
          <div style={{ height: '24px', width: '60px', background: '#e2e8f0', borderRadius: '6px' }} />
        </div>
        <div style={{ height: '40px', background: '#e2e8f0', borderRadius: '10px', marginTop: '8px' }} />
      </div>
    </div>
  )
}

/* ─── Empty state ─────────────────────────────────────────────────────── */
function EmptyState({ onClear, op }) {
  return (
    <div style={{
      gridColumn: '1 / -1',
      textAlign: 'center', padding: '64px 20px',
      background: '#fafbfc', borderRadius: '20px',
      border: '2px dashed rgba(0,46,83,0.1)',
    }}>
      <div style={{ fontSize: '52px', marginBottom: '16px' }}>📋</div>
      <div style={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: NAVY, marginBottom: '10px' }}>
        {op.noMatchTitle}
      </div>
      <div style={{ fontFamily: FONT, fontSize: '14px', color: '#6b7a8d', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px', lineHeight: 1.6 }}>
        {op.noMatchDesc}
      </div>
      <button
        id="empty-state-clear-filters"
        onClick={onClear}
        style={{
          background: GOLD, color: '#fff', border: 'none',
          fontFamily: FONT, fontSize: '14px', fontWeight: 700,
          padding: '13px 28px', borderRadius: '10px', cursor: 'pointer',
          transition: 'background 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#d4af6a'}
        onMouseLeave={e => e.currentTarget.style.background = GOLD}
      >
        {op.clearFilters}
      </button>
    </div>
  )
}

/* ─── Why Buy Off-Plan items — now dynamic via translations ───────────── */

/* ─── Pagination ──────────────────────────────────────────────────────── */
function Pagination({ page, total, limit, onPageChange }) {
  const totalPages = Math.ceil(total / limit)
  if (totalPages <= 1) return null

  const pages = []
  let start = Math.max(1, page - 2)
  let end = Math.min(totalPages, page + 2)
  
  if (page <= 2) end = Math.min(5, totalPages)
  if (page >= totalPages - 1) start = Math.max(1, totalPages - 4)

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  const btnStyle = (disabled, active) => ({
    padding: '8px 14px',
    border: `1px solid ${active ? GOLD : 'rgba(0,46,83,0.1)'}`,
    background: active ? GOLD : disabled ? '#f8fafc' : '#fff',
    color: active ? '#fff' : disabled ? '#94a3b8' : NAVY,
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: FONT,
    fontSize: '14px',
    fontWeight: 600,
    transition: 'all 0.2s',
  })

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '48px' }}>
      <button onClick={() => onPageChange(1)} disabled={page === 1} style={btnStyle(page === 1, false)}>&laquo;</button>
      <button onClick={() => onPageChange(page - 1)} disabled={page === 1} style={btnStyle(page === 1, false)}>&lsaquo;</button>
      
      {pages.map(p => (
        <button key={p} onClick={() => onPageChange(p)} style={btnStyle(false, page === p)}>
          {p}
        </button>
      ))}

      <button onClick={() => onPageChange(page + 1)} disabled={page === totalPages} style={btnStyle(page === totalPages, false)}>&rsaquo;</button>
      <button onClick={() => onPageChange(totalPages)} disabled={page === totalPages} style={btnStyle(page === totalPages, false)}>&raquo;</button>
    </div>
  )
}

/* ─── Main Page ───────────────────────────────────────────────────────── */
function OffplanPage() {
  const { t } = useLanguage()
  const op = t('offplan')
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '')
    return {
      ...DEFAULT_FILTERS,
      developer: params.get('developer') || ''
    }
  })
  const [page, setPage]               = useState(1)
  const [projects, setProjects]       = useState([])
  const [allDevelopers, setAllDevs]   = useState([])
  const [total, setTotal]             = useState(0)
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState(null)
  const [allAreas, setAllAreas]       = useState([])
  const [allEmirates, setAllEmirates] = useState([])
  const debounceRef                   = useRef(null)

  // ── Fetch projects whenever server-side filters change ────────────────
  // fetchProjects: called with current filters, always fresh (no stale closure)
  const fetchProjects = useCallback((f, currentPage) => {
    setLoading(true)
    setError(null)

    const params = new URLSearchParams({ limit: 30, offset: (currentPage - 1) * 30 })
    if (f.searchQuery)              params.set('search_query', f.searchQuery)
    if (f.developer)                params.set('developer', f.developer)
    if (f.bedrooms.length > 0)      params.set('unit_bedrooms', f.bedrooms.join(','))
    if (f.minPrice > PRICE_MIN_ABS) params.set('unit_price_from', f.minPrice)
    if (f.maxPrice < PRICE_MAX_ABS) params.set('unit_price_to', f.maxPrice)
    if (f.lifestyles.length > 0)    params.set('lifestyles', f.lifestyles.join(','))
    if (f.areas.length > 0)         params.set('areas', f.areas.join(','))
    if (f.emirates.length > 0)      params.set('emirates', f.emirates.join(','))
    if (f.sortBy && f.sortBy !== 'default') params.set('sort_by', f.sortBy)
    // Always pass currency so backend converts prices correctly
    params.set('preferred_currency', f.currency)
    // Translate internal m² symbol to API-expected 'm2'
    if (f.areaUnit !== 'sqft')      params.set('preferred_area_unit', 'm2')

    fetch(`${API_URL}/api/offplan?${params}`)
      .then(res => { if (!res.ok) throw new Error(`Server responded with ${res.status}`); return res.json() })
      .then(data => {
        setProjects(data.projects || [])
        setTotal(data.total || 0)
        if (data.developerList?.length > 0) setAllDevs(data.developerList)
        if (data.areaList?.length > 0) setAllAreas(data.areaList)
        if (data.emirateList?.length > 0) setAllEmirates(data.emirateList)
        setLoading(false)
      })
      .catch(err => {
        console.error('[OffplanPage] API fetch error:', err)
        setError('Unable to load projects right now. Please try again shortly.')
        setLoading(false)
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-fetch only when server-side filter params change.
  // Developer is now fetched server-side as well structure changes.
  useEffect(() => {
    clearTimeout(debounceRef.current)
    const delay = filters.searchQuery ? 500 : 0 // debounce only search input
    debounceRef.current = setTimeout(() => fetchProjects(filters, page), delay)
    return () => clearTimeout(debounceRef.current)
  }, [
    filters.searchQuery, filters.bedrooms, filters.minPrice, filters.maxPrice,
    filters.currency, filters.areaUnit, filters.developer, filters.lifestyles, filters.areas, filters.emirates, page, fetchProjects,
  ])

  // ── Scroll to hash on mount ───────────────────────────────────────────
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const targetId = window.location.hash.substring(1)
      const scrollToTarget = () => {
        const element = document.getElementById(targetId)
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - 80
          window.scrollTo({ top: y, behavior: 'smooth' })
        }
      }
      
      // Try after a brief moment to allow DOM render
      setTimeout(scrollToTarget, 100)
      // And again slightly later to account for data loading shifts
      setTimeout(scrollToTarget, 800)
    }
  }, [])

  const visibleProjects = projects

  function resetFilters() {
    setFilters(DEFAULT_FILTERS)
    setPage(1)
  }

  function handleFilterChange(newFilters) {
    setFilters(newFilters)
    setPage(1)
  }

  return (
    <div style={{ fontFamily: FONT }}>
      <Navbar />

      {/* ══ HERO ══════════════════════════════════════════════════════════ */}
      <div style={{
        position: 'relative', width: '100%',
        height: '100vh', minHeight: '620px', maxHeight: '800px',
        overflow: 'hidden', display: 'flex', alignItems: 'center',
      }}>
        <img
          src="/assets/sell/downtown.webp"
          alt="Dubai Skyline"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%' }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to right, rgba(0,20,40,0.88) 0%, rgba(0,20,40,0.55) 60%, rgba(0,20,40,0.3) 100%)',
        }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', background: `linear-gradient(to right, ${GOLD}, transparent)` }} />

        <div style={{
          position: 'relative', zIndex: 1,
          maxWidth: '1200px', margin: '0 auto', padding: '0 40px', paddingTop: '110px',
          width: '100%', boxSizing: 'border-box',
        }}>
          <div style={{ maxWidth: '620px' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: `${GOLD}22`, border: `1px solid ${GOLD}50`,
              color: GOLD, fontFamily: FONT, fontSize: '11px', fontWeight: 700,
              letterSpacing: '2px', textTransform: 'uppercase',
              padding: '6px 16px', borderRadius: '50px', marginBottom: '22px',
            }}>
              {op.heroBadge}
            </div>

            <h1 style={{
              fontFamily: FONT, fontSize: 'clamp(34px, 5vw, 60px)',
              fontWeight: 800, color: '#fff', margin: '0 0 16px', lineHeight: 1.1, letterSpacing: '-1px',
            }}>
              {op.heroLine1}<br />
              <span style={{ color: GOLD }}>{op.heroAccent}</span>
            </h1>

            <p style={{
              fontFamily: FONT, fontSize: '17px', color: 'rgba(255,255,255,0.68)',
              margin: '0 0 36px', lineHeight: 1.7, maxWidth: '500px',
            }}>
              {op.heroSub}
            </p>

            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              <a href="#projects" style={{
                display: 'inline-block', background: GOLD, color: '#fff',
                fontFamily: FONT, fontSize: '15px', fontWeight: 700,
                padding: '15px 32px', borderRadius: '10px', textDecoration: 'none',
                transition: 'background 0.2s, transform 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = '#d4af6a'; e.currentTarget.style.transform = 'translateY(-2px)' }}
                onMouseLeave={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.transform = 'translateY(0)' }}
              >
                {op.browseBtn}
              </a>
              <a href="/contact" style={{
                display: 'inline-block',
                background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: '#fff', fontFamily: FONT, fontSize: '15px', fontWeight: 600,
                padding: '15px 32px', borderRadius: '10px', textDecoration: 'none',
                transition: 'background 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              >
                {op.advisorBtn}
              </a>
            </div>
          </div>

          {/* Floating stat chips */}
          <div style={{
            position: 'absolute', right: '40px', top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex', flexDirection: 'column', gap: '12px',
          }} className="offplan-hero-stats">
            {[
              { num: '30%+', label: op.statGrowth },
              { num: 'AED 2M', label: op.statVisa },
              { num: '0%', label: op.statComm },
            ].map((stat, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px', padding: '16px 22px', textAlign: 'center', minWidth: '150px',
              }}>
                <div style={{ fontFamily: "'Georgia', serif", fontSize: '24px', fontWeight: 700, color: GOLD }}>{stat.num}</div>
                <div style={{ fontFamily: FONT, fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginTop: '3px', letterSpacing: '0.5px' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══ WHY BUY OFF-PLAN ══════════════════════════════════════════════ */}
      <div style={{ background: '#f4f7fb', padding: '64px 20px 60px', boxSizing: 'border-box' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '44px' }}>
            <span style={{
              display: 'inline-block', background: `${GOLD}18`, color: GOLD,
              fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
              padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
              border: `1px solid ${GOLD}35`, marginBottom: '12px',
            }}>{op.whyBadge}</span>
            <h2 style={{ fontFamily: FONT, fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 700, color: NAVY, margin: 0 }}>
              {op.whyHeading}
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }} className="op-why-grid">
            {op.why.map((item, i) => (
              <div key={i} style={{
                background: '#fff', borderRadius: '16px', padding: '28px 22px',
                border: '1px solid rgba(0,46,83,0.07)',
                transition: 'all 0.3s', cursor: 'default',
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(197,160,89,0.14)'; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = `${GOLD}40` }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(0,46,83,0.07)' }}
              >
                <div style={{ fontSize: '28px', marginBottom: '14px' }}>{item.icon}</div>
                <div style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY, marginBottom: '8px' }}>{item.title}</div>
                <div style={{ fontFamily: FONT, fontSize: '13px', color: '#6b7a8d', lineHeight: 1.65 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══ FILTER BAR + PROJECTS ═════════════════════════════════════════ */}
      <div id="projects" style={{ background: '#fff' }}>

        {/* Section header (above sticky bar) */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '56px 20px 0', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <span style={{
                display: 'inline-block', background: `${GOLD}18`, color: GOLD,
                fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
                padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
                border: `1px solid ${GOLD}35`, marginBottom: '10px',
              }}>{op.featuredBadge}</span>
              <h2 style={{ fontFamily: FONT, fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 700, color: NAVY, margin: 0 }}>
                {op.featuredHeading}
              </h2>
            </div>
            <a href="/contact" style={{
              display: 'inline-block', color: NAVY,
              fontFamily: FONT, fontSize: '14px', fontWeight: 600,
              textDecoration: 'none', borderBottom: `2px solid ${GOLD}`,
              paddingBottom: '2px', transition: 'color 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.color = GOLD}
              onMouseLeave={e => e.currentTarget.style.color = NAVY}
            >
              {op.catalogueLink}
            </a>
          </div>
        </div>

        {/* Sticky filter bar */}
        <OffplanFilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          developerList={allDevelopers}
          areaList={allAreas}
          emirateList={allEmirates}
          resultCount={visibleProjects.length}
          totalCount={total}
          loading={loading}
        />

        {/* Shimmer keyframe */}
        <style>{`
          @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
          @media (max-width: 1024px) {
            .offplan-hero-stats { display: none !important; }
            .op-grid { grid-template-columns: repeat(2, 1fr) !important; }
            .op-why-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
          @media (max-width: 600px) {
            .op-grid { grid-template-columns: 1fr !important; }
            .op-why-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>

        {/* Project grid */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 72px', boxSizing: 'border-box' }}>
          {error && (
            <div style={{
              textAlign: 'center', padding: '48px 20px',
              background: '#fff8f8', borderRadius: '16px',
              border: '1px solid #fecaca', color: '#991b1b',
              fontFamily: FONT, fontSize: '15px',
            }}>
              ⚠️ {error}
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
          }} className="op-grid">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
              : visibleProjects.length === 0
                ? <EmptyState onClear={resetFilters} op={op} />
                : visibleProjects.map((p, i) => <PropertyCard key={p.id || i} project={p} />)
            }
          </div>
          
          {!loading && visibleProjects.length > 0 && (
            <Pagination page={page} total={total} limit={30} onPageChange={setPage} />
          )}
        </div>
      </div>

      {/* ══ EXPLORE COMMUNITIES ═══════════════════════════════════════════ */}
      <div style={{ background: '#fff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px 0', boxSizing: 'border-box' }}>
          <div style={{ marginBottom: '0px' }}>
            <span style={{
              display: 'inline-block', background: `${GOLD}18`, color: GOLD,
              fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
              padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
              border: `1px solid ${GOLD}35`, marginBottom: '10px',
            }}>{op.communitiesBadge}</span>
            <h2 style={{ fontFamily: FONT, fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 700, color: NAVY, margin: 0 }}>
              {op.communitiesHeading}
            </h2>
          </div>
        </div>
        <LocationScroller locations={op.locations} seeListings={op.seeListings} />
      </div>

      {/* ══ ROI CALCULATOR PROMO ══════════════════════════════════════════ */}
      <div style={{ background: '#f4f7fb', padding: '60px 20px', boxSizing: 'border-box' }}>
        <div style={{
          maxWidth: '1200px', margin: '0 auto',
          background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`,
          borderRadius: '20px', padding: '52px 52px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: '28px',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* decorative orbs */}
          <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '260px', height: '260px', borderRadius: '50%', background: `${GOLD}10`, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '180px', height: '180px', borderRadius: '50%', background: `${GOLD}08`, pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <span style={{
              display: 'inline-block', background: `${GOLD}25`, color: GOLD,
              fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
              padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
              border: `1px solid ${GOLD}40`, marginBottom: '14px',
            }}>
              📊 Investment Tools
            </span>
            <div style={{ fontFamily: FONT, fontSize: 'clamp(20px, 2.5vw, 28px)', fontWeight: 700, color: '#fff', marginBottom: '10px', lineHeight: 1.2 }}>
              Calculate Your ROI Before You Invest
            </div>
            <p style={{ fontFamily: FONT, fontSize: '15px', color: 'rgba(255,255,255,0.7)', margin: 0, lineHeight: 1.7, maxWidth: '480px' }}>
              Estimate gross yield, net yield, capital appreciation, and total return on any Dubai property — in seconds.
            </p>
          </div>

          <a href="/roi-calculator"
            style={{
              position: 'relative', zIndex: 1,
              display: 'inline-block', background: GOLD, color: '#fff',
              fontFamily: FONT, fontSize: '15px', fontWeight: 700,
              padding: '16px 38px', borderRadius: '10px', textDecoration: 'none',
              whiteSpace: 'nowrap', transition: 'background 0.2s, transform 0.15s',
              boxShadow: `0 6px 20px rgba(197,160,89,0.4)`,
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#d4af6a'; e.currentTarget.style.transform = 'translateY(-2px)' }}
            onMouseLeave={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.transform = 'translateY(0)' }}
          >
            Open ROI Calculator →
          </a>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default OffplanPage
